import { useEffect, useRef } from "react";

import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { setBackForwardNavigationGestures } from "capacitor-plugin-ios-webview-configurator";

interface Options {
  isOpen: boolean;
  onBack: () => void;
}

const EDGE_THRESHOLD = 30;
const SWIPE_MIN_DISTANCE = 80;
const ACTIVATION_DELAY = 300;

/**
 * Wires native back-gesture handling for a Redux/state-driven modal so that:
 * - iOS WKWebView swipe-back closes the modal instead of changing the URL
 * - Android hardware back closes the modal instead of popping history
 *
 * Mirrors the pattern already used in OperateModal so behavior stays consistent
 * across the app's full-screen mobile modals.
 */
export const useModalBackGesture = ({ isOpen, onBack }: Options) => {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  // iOS: replace WKWebView swipe-back with manual edge-swipe detection
  useEffect(() => {
    if (Capacitor.getPlatform() !== "ios" || !isOpen) return;

    setBackForwardNavigationGestures(false);

    let touchStartX = 0;
    let touchStartY = 0;
    // Activation delay avoids false positives from the touch that opened the modal
    let isListening = false;
    const activationTimer = setTimeout(() => {
      isListening = true;
    }, ACTIVATION_DELAY);

    const handleTouchStart = (e: TouchEvent) => {
      if (!isListening) return;
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isListening) return;
      if (touchStartX > EDGE_THRESHOLD || touchStartX === 0) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = Math.abs(touch.clientY - touchStartY);

      if (deltaX > SWIPE_MIN_DISTANCE && deltaX > deltaY) {
        onBackRef.current();
      }
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      clearTimeout(activationTimer);
      setBackForwardNavigationGestures(true);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isOpen]);

  // Android: hardware back button
  useEffect(() => {
    if (Capacitor.getPlatform() !== "android" || !isOpen) return;

    let backButtonListener: { remove: () => void } | undefined;

    void (async () => {
      backButtonListener = await CapApp.addListener("backButton", () => {
        onBackRef.current();
      });
    })();

    return () => {
      backButtonListener?.remove();
    };
  }, [isOpen]);
};
